import { useEffect, useMemo, useState } from "react";
import { getUserProfile } from "../services/userProfileService";
import { UserProfile } from "../types/User";
import { debugLogError } from "../utils/debug-log-error";

export function useGetUserProfiles(userIds: string[], skip: boolean = false) {
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (userIds.length === 0) {
      setProfiles([]);
    }
  }, [userIds]);

  useEffect(() => {
    if (userIds.length === 0 || error || skip) {
      return;
    }

    if (loading || userIds.length === profiles.length) {
      return;
    }

    const fetchProfiles = async () => {
      setLoading(true);

      try {
        const profiles = await Promise.all(
          userIds.map(async (userId) => {
            const profile = await getUserProfile(userId);
            return profile;
          })
        );
        setProfiles(profiles.flatMap((p) => (!!p ? [p] : [])));
      } catch (e) {
        debugLogError("useGetUserProfiles", e);
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [userIds, loading, profiles, error, skip]);

  return useMemo(
    () => ({ profiles, loading, error }),
    [profiles, loading, error]
  );
}
